import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  EligibilityStatus,
  StudySubjectViewSchema,
  SubjectStatus,
  StudySubjectBulkResponseSchema,
  StudySubjectBulkCreateItem,
} from '@common/config/api/client'
import BaseDialog from '@common/components/BaseDialog'
import { OwnSiteStudySubjectCreateSchema, StudySubjectCreateSchema } from '../schemas/studySubjectValidationSchema'
import { Button, Stack, Typography, Switch, TextField as MuiTextField } from '@mui/material'
import { Dropdown } from '@common/components/Form/Dropdown'
import { TextField } from '@common/components/Form/TextField'
import { AddRounded } from '@mui/icons-material'
import { useGetMyStudySite, useGetStudySubjectsMetadata } from '../hooks/useStudySubjectsQueries'
import { useParams } from 'react-router-dom'
import { useGetStudyPrimarySites } from '@features/study_details/sites/hooks/useStudySitesQueries'
import { useCreateStudySubject, useBulkCreateStudySubjects } from '../hooks/useStudySubjectsMutations'
import { handleMutationError } from '@common/hooks/handleMutationError'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'
import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { Option } from '@common/components/Form/Dropdown/Dropdown'
import { useGetStudyById } from '@study_setup/hooks/useStudyQueries'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import { useGetStudySubjectConfiguration } from '@study_setup/SubjectConfiguration/hooks/useStudySubjectConfigurationQueries'
import { CharacterType } from '@study_setup/SubjectConfiguration/types'
import FormRendererDialog from '@features/subject/components/FormRendererDialog'
import { useGetStudyFormById } from '@study_setup/hooks/useStudyFormQueries'
import { useCombinedPermissions } from '@auth/hooks/useCombinedPermissions'
import { SubjectPermission } from '@auth/permissionsEnum'
import { DEFAULT_OVERALL_STATUS, DEFAULT_ELIGIBILITY_STATUS } from '@features/study_details/Subjects/subject'
import Upload from '@features/subject/components/UploadZone'
import { FileSystemItem } from '@features/subject/subject.types'
import { parseSubjectsCSV, parseSubjectsExcel } from '../utils/BulkSubjectsUploadParser'

export interface CreateStudySubjectDialogProps {
  onClose: (data: StudySubjectViewSchema | StudySubjectBulkResponseSchema | undefined) => void
}

type CreateStudySubjectFormValues = {
  pseudo_id: string
  primary_site_pseudo_id: string | null
  primary_site_study_site_id: string | null
  secondary_site_study_site_id: string | null
  status: SubjectStatus
  eligibility_status: EligibilityStatus
  subject_form_id: string | null
  subject_form_submission: string | null
}

type BulkSubjectUploadData = {
  file: File | null
}

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <MuiTextField label={label} value={value} disabled fullWidth slotProps={{ input: { readOnly: true } }} />
)

const CreateStudySubjectDialog = ({ onClose }: CreateStudySubjectDialogProps) => {
  const [isBulkMode, setIsBulkMode] = useState(false)
  const [eFormDialogState, setEFormUploadDialogState] = useState<{
    visible: boolean
    eFormFields?: string | null
    eFormValues?: string | null
    source: 'SUBJECT'
    containerSubmissionId?: string | null
  }>({
    visible: false,
    eFormFields: null,
    eFormValues: null,
    source: 'SUBJECT',
  })
  const [secondarySiteOptions, setSecondarySiteOptions] = useState<Option[]>([])

  const { studyId } = useParams<{ studyId: string }>()
  const { hasPermission, hasAnyPermission } = useCombinedPermissions()

  // Site users can only create subjects for the single site they are assigned to, so they get a
  // read-only view of that site instead of the site selection dropdowns.
  const canSelectSite = hasPermission(SubjectPermission.CREATE)
  const isOwnSiteUser = !canSelectSite

  const { data: subjectMetadata, isLoading: isSubjectMetadataLoading } = useGetStudySubjectsMetadata(studyId!)
  const { data: primarySitesData, isLoading: isPrimarySitesLoading } = useGetStudyPrimarySites(
    {
      study_id: studyId!,
    },
    canSelectSite,
  )
  const { data: myStudySite } = useGetMyStudySite(studyId!, isOwnSiteUser)
  const { data: studyData, isLoading: isStudyDataLoading } = useGetStudyById(studyId!)
  const { data: subjectConfigData } = useGetStudySubjectConfiguration(studyId!)
  const subjectFormId = useMemo(() => subjectConfigData?.study_form_definition_id, [subjectConfigData])

  const ownSiteLabel = [myStudySite?.pseudo_id, myStudySite?.site?.name].filter(Boolean).join(' - ')

  const subjectIdCharacters = subjectConfigData?.subject_validation_number_of_digits
  const acceptedCharacterTypes = subjectConfigData?.subject_validation_accepted_charater_type ?? []
  const isNumericalOnly = acceptedCharacterTypes.length === 1 && acceptedCharacterTypes[0] === CharacterType.NUMERICAL
  const isAlphaOnly = acceptedCharacterTypes.length === 1 && acceptedCharacterTypes[0] === CharacterType.ALPHA
  const subjectIdUnit = isNumericalOnly ? 'digit' : isAlphaOnly ? 'letter' : 'character'
  const subjectIdLabel = subjectIdCharacters
    ? `Subject ID (${subjectIdCharacters} ${subjectIdUnit}${subjectIdCharacters === 1 ? '' : 's'})`
    : 'Subject ID'
  const { data: subjectForm } = useGetStudyFormById(studyId!, subjectFormId!)

  const { mutate: createStudySubject, isPending: isCreatePending } = useCreateStudySubject()
  const { mutate: bulkCreateStudySubjects, isPending: isBulkCreatePending } = useBulkCreateStudySubjects()

  const defaultOverAllStatus = subjectMetadata?.statuses?.some((x) => x.value === DEFAULT_OVERALL_STATUS.value)
    ? DEFAULT_OVERALL_STATUS
    : subjectMetadata?.statuses[0]
  const defaultEligibilityStatus = subjectMetadata?.eligibility_statuses?.some(
    (x) => x.value === DEFAULT_ELIGIBILITY_STATUS.value,
  )
    ? DEFAULT_ELIGIBILITY_STATUS
    : subjectMetadata?.eligibility_statuses[0]

  const singleSubjectForm = useForm<CreateStudySubjectFormValues>({
    resolver: zodResolver(canSelectSite ? StudySubjectCreateSchema : OwnSiteStudySubjectCreateSchema),
    mode: 'onSubmit',
    defaultValues: {
      pseudo_id: '',
      primary_site_pseudo_id: null,
      primary_site_study_site_id: null,
      secondary_site_study_site_id: null,
      status: defaultOverAllStatus?.value as SubjectStatus,
      eligibility_status: defaultEligibilityStatus?.value as EligibilityStatus,
      subject_form_id: subjectFormId ?? null,
      subject_form_submission: null,
    },
  })

  const bulkSubjectForm = useForm<BulkSubjectUploadData>({
    defaultValues: {
      file: null,
    },
  })

  useEffect(() => {
    if (subjectMetadata && !isSubjectMetadataLoading) {
      const defaultOverAllStatus = subjectMetadata.statuses?.some((x) => x.value === DEFAULT_OVERALL_STATUS.value)
        ? DEFAULT_OVERALL_STATUS
        : subjectMetadata.statuses[0]
      const defaultEligibilityStatus = subjectMetadata.eligibility_statuses?.some(
        (x) => x.value === DEFAULT_ELIGIBILITY_STATUS.value,
      )
        ? DEFAULT_ELIGIBILITY_STATUS
        : subjectMetadata.eligibility_statuses[0]

      if (!singleSubjectForm.getValues('status') && defaultOverAllStatus) {
        singleSubjectForm.setValue('status', defaultOverAllStatus.value as SubjectStatus)
      }
      if (!singleSubjectForm.getValues('eligibility_status') && defaultEligibilityStatus) {
        singleSubjectForm.setValue('eligibility_status', defaultEligibilityStatus.value as EligibilityStatus)
      }
    }
  }, [subjectMetadata, isSubjectMetadataLoading, singleSubjectForm])

  const handleSingleSubjectSubmit = async () => {
    const isValid = await singleSubjectForm.trigger()
    if (isValid) {
      const formData = singleSubjectForm.getValues()
      createStudySubject(
        {
          studyId: studyId!,
          subjectPayload: formData,
        },
        {
          onError: handleMutationError,
          onSuccess: (data) => {
            toast(<Toast message="Study subject created successfully!" variant="success" />)
            singleSubjectForm.reset()
            onClose(data)
          },
        },
      )
    }
  }

  const handleBulkSubjectsSubmit = async () => {
    const isValid = await bulkSubjectForm.trigger()
    if (!isValid || !bulkSubjectForm.getValues().file) return

    const file = bulkSubjectForm.getValues().file
    let parsedSubjects: Array<StudySubjectBulkCreateItem> = []

    try {
      if (file) {
        const extension = file.name.split('.').pop()?.toLowerCase()
        if (extension === 'csv') {
          const text = await file.text()
          parsedSubjects = await parseSubjectsCSV(text)
        } else if (extension === 'xlsx' || extension === 'xls') {
          parsedSubjects = await parseSubjectsExcel(file)
        }
      }

      if (parsedSubjects.length > 0) {
        bulkCreateStudySubjects(
          {
            studyId: studyId!,
            subjectsPayload: { subjects: parsedSubjects },
          },
          {
            onSuccess: (data) => {
              const successCount = data.success_count
              const failureCount = data.failure_count

              if (failureCount === 0) {
                toast(<Toast message={`Successfully added ${successCount} subjects.`} variant="success" />)
              } else if (successCount === 0) {
                toast(<Toast message={`Failed to add subjects. Please check your file.`} variant="error" />)
              } else {
                toast(
                  <Toast
                    message={`Added ${successCount} subjects successfully. ${failureCount} subjects failed to be added.`}
                    variant="warning"
                  />,
                )
              }

              bulkSubjectForm.reset()
              onClose(data)
            },
            onError: handleMutationError,
          },
        )
      }
    } catch (error) {
      console.error('Error processing file:', error)
      toast(<Toast message="Error processing file. Please check the format." variant="error" />)
    }
  }

  const handleFormSubmit = async () => {
    if (isBulkMode) {
      await handleBulkSubjectsSubmit()
    } else {
      await handleSingleSubjectSubmit()
    }
  }

  const handlePrimarySiteChange = (_: SyntheticEvent, value: unknown) => {
    if (value && typeof value !== 'string') {
      const selectedOption = value as Option
      singleSubjectForm.setValue('primary_site_study_site_id', selectedOption.value)
      singleSubjectForm.setValue(
        'primary_site_pseudo_id',
        (selectedOption.additional_fields?.site_psuedo_id as string) ?? '',
      )
      singleSubjectForm.setValue('secondary_site_study_site_id', null)
      const secondarySites = (selectedOption.additional_fields?.secondary_sites as Option[]) ?? []
      setSecondarySiteOptions(secondarySites)
    } else {
      setSecondarySiteOptions([])
      singleSubjectForm.setValue('primary_site_study_site_id', null)
      singleSubjectForm.setValue('primary_site_pseudo_id', null)
      singleSubjectForm.setValue('secondary_site_study_site_id', null)
    }
  }

  const handleSubmitEForm = async (data: Record<string, string | number | boolean>) => {
    singleSubjectForm.setValue('subject_form_submission', JSON.stringify(data))
    setEFormUploadDialogState({
      ...eFormDialogState,
      eFormValues: JSON.stringify(data),
      visible: false,
    })
  }

  const handleBulkModeSwitch = () => {
    setIsBulkMode(!isBulkMode)
  }

  useEffect(() => {
    setEFormUploadDialogState({
      ...eFormDialogState,
      eFormFields: JSON.stringify(subjectForm?.fields),
    })
    singleSubjectForm.setValue('subject_form_id', subjectFormId ?? null)
  }, [subjectForm])

  const renderSingleSubjectForm = () => (
    <Stack spacing={SPACING.spacingLg}>
      {canSelectSite && (
        <>
          <Typography>1. Site information</Typography>
          <Stack direction="row" spacing={SPACING.formFieldsInnerSectionSpacing}>
            <Dropdown
              control={singleSubjectForm.control}
              name="primary_site_study_site_id"
              label="Primary Site ID"
              options={primarySitesData?.primary_sites || []}
              loading={isPrimarySitesLoading}
              onChange={handlePrimarySiteChange}
            />
            {!isStudyDataLoading && studyData?.subjects_required_to_visit_multiple_sites && (
              <Dropdown
                control={singleSubjectForm.control}
                name="secondary_site_study_site_id"
                label="Secondary Site ID"
                options={secondarySiteOptions}
                loading={isPrimarySitesLoading}
                disabled={!singleSubjectForm.getValues('primary_site_study_site_id')}
              />
            )}
          </Stack>
        </>
      )}
      {isOwnSiteUser && (
        <>
          <Typography>1. Site information</Typography>
          <ReadOnlyField label="Primary Site ID" value={ownSiteLabel} />
        </>
      )}
      <Typography>2. Subject information</Typography>
      <TextField control={singleSubjectForm.control} name="pseudo_id" label={subjectIdLabel} />
      {!isOwnSiteUser && (
        <Dropdown
          control={singleSubjectForm.control}
          name="status"
          label="Status"
          options={subjectMetadata?.statuses || []}
          loading={isSubjectMetadataLoading}
        />
      )}
      {!isOwnSiteUser && (
        <Dropdown
          control={singleSubjectForm.control}
          name="eligibility_status"
          label="Eligibility Status"
          options={subjectMetadata?.eligibility_statuses || []}
          loading={isSubjectMetadataLoading}
        />
      )}
      {subjectConfigData?.study_form_definition_id && (
        <>
          <Typography>Subject eForm</Typography>
          <Button
            sx={{ borderStyle: 'dashed', borderColor: 'secondary.dark', borderWidth: 1, borderRadius: 2 }}
            onClick={() =>
              setEFormUploadDialogState({
                ...eFormDialogState,
                visible: true,
              })
            }
          >
            <AddRounded sx={{ color: 'black' }} />
            <Typography variant="button" color="black">
              Fill out eForm
            </Typography>
          </Button>
        </>
      )}
    </Stack>
  )

  const renderBulkSubjectForm = () => (
    <Stack spacing={SPACING.spacingXl}>
      <Upload
        fileTypesAllowed={['.csv', '.xlsx']}
        onDrop={(acceptedFiles) => {
          if (acceptedFiles.length > 0) {
            bulkSubjectForm.setValue('file', acceptedFiles[0])
          }
        }}
        data={
          bulkSubjectForm.watch('file')
            ? [
                {
                  id: '1',
                  name: bulkSubjectForm.watch('file')?.name || '',
                  size: bulkSubjectForm.watch('file')?.size || 0,
                  type: bulkSubjectForm.watch('file')?.type || '',
                } as FileSystemItem,
              ]
            : []
        }
        onRemove={() => {
          bulkSubjectForm.setValue('file', null)
        }}
        maxSize={3 * 1024 * 1024}
      />
    </Stack>
  )

  return (
    <BaseDialog
      title="Add new subject"
      description="Provide the new subject's details below"
      open={true}
      onClose={() => onClose(undefined)}
      onSubmit={handleFormSubmit}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      form={isBulkMode ? bulkSubjectForm : singleSubjectForm}
      submitLabel={isBulkMode ? 'Add Subjects' : 'Create Subject'}
      isSubmitting={isBulkMode ? isBulkCreatePending : isCreatePending}
      width={SIZING.lgModalWidth}
    >
      <Stack spacing={SPACING.spacingXl}>
        {hasAnyPermission([SubjectPermission.INGEST_ALL, SubjectPermission.INGEST_OWN]) && (
          <Stack
            direction="row"
            gap="10px"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: SPACING.borderRadiusLg,
              padding: SPACING.spacingLg,
            }}
            alignItems="center"
          >
            <Switch checked={isBulkMode} onChange={handleBulkModeSwitch} sx={{ marginRight: 0, paddingX: 2 }} />
            <Typography>Bulk subject upload</Typography>
          </Stack>
        )}

        {isBulkMode ? renderBulkSubjectForm() : renderSingleSubjectForm()}
      </Stack>

      {eFormDialogState.visible && (
        <FormRendererDialog
          header="Fill eForm"
          onClose={() => setEFormUploadDialogState({ ...eFormDialogState, visible: false })}
          fields={eFormDialogState.eFormFields}
          values={eFormDialogState.eFormValues}
          onSubmit={handleSubmitEForm}
        />
      )}
    </BaseDialog>
  )
}

export default CreateStudySubjectDialog
